import { useEffect, useState } from 'react';
import {
  QuotaStatus,
  saveDirectFkGoals,
  saveGoalSeconds,
  saveKeeperGoals,
  saveOlimpicoGoals,
  useAllMatchStatsWC26,
  useBracketFixtures,
  useDirectFkGoals,
  useGoalSeconds,
  useKeeperGoals,
  useOlimpicoGoals,
  useQuotaStatus,
} from '../../queries';
import { BracketFixture, MatchStats } from '../../types';
import { goalKey } from '../../utils/scoringUtils';
import './WC26Admin.css';

type GoalType = 'directFk' | 'olimpico' | 'keeperGoal' | 'goalTimes';

const GOAL_TYPE_LABELS: Record<GoalType, string> = {
  directFk: 'Direct Free Kick',
  olimpico: 'Olimpico (Direct Corner)',
  keeperGoal: 'Goalkeeper Goal',
  goalTimes: 'Goal Times',
};

interface GoalSectionProps {
  type: GoalType;
  matchStats: MatchStats[];
  existing: string[] | undefined;
  fixturesById: Record<number, BracketFixture>;
}

function GoalSection({
  type, matchStats, existing, fixturesById,
}: GoalSectionProps) {
  const [marked, setMarked] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (existing) setMarked(new Set(existing));
  }, [existing]);

  const toggle = (key: string) => {
    setMarked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const keys = Array.from(marked);
      if (type === 'directFk') await saveDirectFkGoals(keys);
      else if (type === 'olimpico') await saveOlimpicoGoals(keys);
      else await saveKeeperGoals(keys);
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wc26-admin-section">
      <div className="wc26-admin-section-header">
        <h2>{GOAL_TYPE_LABELS[type]}</h2>
        <button
          type="button"
          className="wc26-admin-save"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        {saved && <span className="wc26-admin-saved">✓ Saved</span>}
      </div>

      {matchStats.length === 0 && (
        <p className="wc26-admin-empty">No match data yet.</p>
      )}

      {[...matchStats].sort((a, b) => {
        const dateA = fixturesById[a.id]?.date;
        const dateB = fixturesById[b.id]?.date;
        if (!dateA || !dateB) return b.id - a.id;
        return new Date(dateB).getTime() - new Date(dateA).getTime();
      }).map((match) => {
        const fixture = fixturesById[match.id];
        const home = match.statistics[0];
        const away = match.statistics[1];
        const kickoff = fixture ? new Date(fixture.date).toLocaleString([], {
          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
        }) : '';
        return (
          <div key={match.id} className="wc26-admin-match">
            <div className="wc26-admin-match-header">
              <span className="wc26-admin-match-teams">
                {fixture && (
                <img className="wc26-admin-match-flag" src={fixture.home.logo} alt={fixture.home.name} />
                )}
                <span>{fixture ? fixture.home.name : home?.name}</span>
                <span className="wc26-admin-match-score">
                  {`${match.score.home} – ${match.score.away}`}
                </span>
                <span>{fixture ? fixture.away.name : away?.name}</span>
                {fixture && (
                <img className="wc26-admin-match-flag" src={fixture.away.logo} alt={fixture.away.name} />
                )}
              </span>
              <span className="wc26-admin-match-kickoff">
                {kickoff && `${kickoff} · `}
                {`#${match.id}`}
              </span>
            </div>
            <div className="wc26-admin-goals">
              {match.goalEvents.length === 0
                ? <p className="wc26-admin-no-goals">No goals</p>
                : match.goalEvents.map((goal) => {
                  const key = goalKey(match.id, goal);
                  const inputId = `${type}-${key}`;
                  const timeStr = goal.extra ? `${goal.time}+${goal.extra}'` : `${goal.time}'`;
                  return (
                    <label key={key} htmlFor={inputId} className="wc26-admin-goal">
                      <input
                        id={inputId}
                        type="checkbox"
                        checked={marked.has(key)}
                        onChange={() => toggle(key)}
                      />
                      <span className="wc26-admin-goal-time">{timeStr}</span>
                      <span className="wc26-admin-goal-player">{goal.playerName}</span>
                      <span className="wc26-admin-goal-team">{goal.teamName}</span>
                      <span className="wc26-admin-goal-type">{goal.type}</span>
                    </label>
                  );
                })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface GoalTimesSectionProps {
  matchStats: MatchStats[];
  existing: Record<string, number> | undefined;
  fixturesById: Record<number, BracketFixture>;
}

function GoalTimesSection({ matchStats, existing, fixturesById }: GoalTimesSectionProps) {
  const [secondsMap, setSecondsMap] = useState<Record<string, number>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (existing) {
      setSecondsMap(existing);
      const initial: Record<string, string> = {};
      Object.entries(existing).forEach(([k, total]) => {
        initial[k] = `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
      });
      setDrafts(initial);
    }
  }, [existing]);

  const parseMmSs = (val: string): number | null => {
    const trimmed = val.trim();
    if (!trimmed) return null;
    const m = trimmed.match(/^(\d{1,3}):(\d{1,2})$/);
    if (!m) return null;
    const secs = parseInt(m[2], 10);
    if (secs > 59) return null;
    return parseInt(m[1], 10) * 60 + secs;
  };

  const handleChange = (key: string, val: string) => {
    setDrafts((prev) => ({ ...prev, [key]: val }));
    setSaved(false);
  };

  const handleBlur = (key: string) => {
    const val = drafts[key] ?? '';
    const total = parseMmSs(val);
    setSecondsMap((prev) => {
      const next = { ...prev };
      if (total === null) delete next[key];
      else next[key] = total;
      return next;
    });
    if (total !== null) {
      setDrafts((prev) => ({
        ...prev,
        [key]: `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`,
      }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveGoalSeconds(secondsMap);
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  const sorted = [...matchStats].sort((a, b) => {
    const dateA = fixturesById[a.id]?.date;
    const dateB = fixturesById[b.id]?.date;
    if (!dateA || !dateB) return a.id - b.id;
    return new Date(dateA).getTime() - new Date(dateB).getTime();
  });

  return (
    <div className="wc26-admin-section">
      <div className="wc26-admin-section-header">
        <h2>Goal Times</h2>
        <button type="button" className="wc26-admin-save" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
        {saved && <span className="wc26-admin-saved">✓ Saved</span>}
      </div>
      <p className="wc26-admin-subtitle">Enter exact time as MM:SS (e.g. 1:23). Overrides the API minute entirely.</p>
      {sorted.map((match) => {
        const fixture = fixturesById[match.id];
        const kickoff = fixture ? new Date(fixture.date).toLocaleString([], {
          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
        }) : '';
        const goals = match.goalEvents.filter(
          (g) => g.type !== 'Own Goal' && g.type !== 'Missed Penalty'
        );
        if (goals.length === 0) return null;
        return (
          <div key={match.id} className="wc26-admin-match">
            <div className="wc26-admin-match-header">
              <span className="wc26-admin-match-teams">
                {fixture && <img className="wc26-admin-match-flag" src={fixture.home.logo} alt={fixture.home.name} />}
                <span>{fixture ? fixture.home.name : match.statistics[0]?.name}</span>
                <span className="wc26-admin-match-score">{`${match.score.home} – ${match.score.away}`}</span>
                <span>{fixture ? fixture.away.name : match.statistics[1]?.name}</span>
                {fixture && <img className="wc26-admin-match-flag" src={fixture.away.logo} alt={fixture.away.name} />}
              </span>
              <span className="wc26-admin-match-kickoff">
                {kickoff && `${kickoff} · `}
                {`#${match.id}`}
              </span>
            </div>
            <div className="wc26-admin-goals">
              {goals.map((goal) => {
                const key = goalKey(match.id, goal);
                const timeStr = goal.extra ? `${goal.time}+${goal.extra}'` : `${goal.time}'`;
                return (
                  <div key={key} className="wc26-admin-goal wc26-admin-goal-time-row">
                    <span className="wc26-admin-goal-time">{timeStr}</span>
                    <span className="wc26-admin-goal-player">{goal.playerName}</span>
                    <span className="wc26-admin-goal-team">{goal.teamName}</span>
                    <label className="wc26-admin-seconds-label" htmlFor={`sec-${key}`}>
                      <input
                        id={`sec-${key}`}
                        type="text"
                        value={drafts[key] ?? ''}
                        placeholder="M:SS"
                        className="wc26-admin-seconds-input"
                        onBlur={() => handleBlur(key)}
                        onChange={(e) => handleChange(key, e.target.value)}
                      />
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function QuotaBanner({ quota }: { quota: QuotaStatus }) {
  const pct = Math.round((quota.remaining / quota.limit) * 100);
  const color = pct > 30 ? '#04cf5c' : pct > 10 ? '#f5a623' : '#e74c3c';
  const ts = new Date(quota.timestamp).toLocaleString([], {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  return (
    <div className="wc26-admin-quota">
      <span className="wc26-admin-quota-label">API Quota</span>
      <span className="wc26-admin-quota-bar-wrap">
        <span className="wc26-admin-quota-bar" style={{ width: `${pct}%`, background: color }} />
      </span>
      <span style={{ color }} className="wc26-admin-quota-count">
        {quota.remaining}
        {' / '}
        {quota.limit}
      </span>
      <span className="wc26-admin-quota-reset">
        resets in approx.
        {' '}
        {Math.ceil(quota.resetInSeconds / 60)}
        m
      </span>
      <span className="wc26-admin-quota-ts">{ts}</span>
    </div>
  );
}

function WC26Admin() {
  const [matchStatsLoading, matchStats] = useAllMatchStatsWC26();
  const [, fixtures] = useBracketFixtures();
  const [, existingDirectFk] = useDirectFkGoals();
  const [, existingOlimpico] = useOlimpicoGoals();
  const [, existingKeeper] = useKeeperGoals();
  const [, existingGoalSeconds] = useGoalSeconds();
  const [, quotaStatus] = useQuotaStatus();
  const [activeTab, setActiveTab] = useState<GoalType>('directFk');

  useEffect(() => {
    const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (favicon) favicon.href = '/favicon_wc.ico';
  }, []);

  const stats = matchStats ?? [];
  const fixturesById = (fixtures ?? []).reduce<Record<number, BracketFixture>>((acc, f) => {
    acc[f.id] = f;
    return acc;
  }, {});

  return (
    <div className="wc26-admin">
      <h1>WC26 Admin — Special Goals</h1>
      <p className="wc26-admin-subtitle">
        Mark goals by type. Each section saves independently.
      </p>
      {quotaStatus && <QuotaBanner quota={quotaStatus} />}

      <div className="wc26-admin-tabs">
        {(Object.keys(GOAL_TYPE_LABELS) as GoalType[]).map((type) => (
          <button
            key={type}
            type="button"
            className={`wc26-admin-tab ${activeTab === type ? 'active' : ''}`}
            onClick={() => setActiveTab(type)}
          >
            {GOAL_TYPE_LABELS[type]}
          </button>
        ))}
      </div>

      {matchStatsLoading && <p className="wc26-admin-empty">Loading match data...</p>}

      {!matchStatsLoading && (
        <>
          {activeTab === 'directFk' && (
            <GoalSection type="directFk" matchStats={stats} existing={existingDirectFk} fixturesById={fixturesById} />
          )}
          {activeTab === 'olimpico' && (
            <GoalSection type="olimpico" matchStats={stats} existing={existingOlimpico} fixturesById={fixturesById} />
          )}
          {activeTab === 'keeperGoal' && (
            <GoalSection type="keeperGoal" matchStats={stats} existing={existingKeeper} fixturesById={fixturesById} />
          )}
          {activeTab === 'goalTimes' && (
            <GoalTimesSection
              matchStats={stats}
              existing={existingGoalSeconds}
              fixturesById={fixturesById}
            />
          )}
        </>
      )}
    </div>
  );
}

export default WC26Admin;
